import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { MJConfirmService } from '@memberjunction/ng-ui-components';
import { OEP_ENTITY, OEP_LIVE_FILTER, type OEPDraftOrSubmitted } from 'agu_oep_core';
import type { FeedbackPlatformNominationEntity, FeedbackPlatformNominationResponseEntity } from 'mj_generatedentities';
import { OEPViewBase } from '../../state/oep-view-base';
import { OEPDataService } from '../../data/oep-data.service';
import type { OEPStatusLine } from '../shared/status';
import { OEPPartialSaveError } from '../shared/partial-save';
import { PartialSaveMessage, QuestionShortLabel } from '../review/review.vm';
import {
    BuildNominationVM, CanSubmitNomination, OEP_OUTSTANDING,
    type NominationInputs, type NominationVM, type OEPNomAnswer, type OEPNomQuestionInput,
} from './nomination.vm';
import { NumOrNull, Str, StrOrNull, Up } from '../shared/rows';

const NOMINATION_FORM = `FormType='Nomination' AND SeasonID IS NULL AND Status='Active'`;
const asStatus = (v: string | null): OEPDraftOrSubmitted | null => (v === 'Draft' || v === 'Submitted' ? v : null);

/**
 * Nomination form ("Nominate" → one presentation) — markup.html:711-760. All copy and rules live in
 * nomination.vm.ts (pure, spec'd). Load gathers NominationInputs in ONE RunViews batch
 * (OEPDataService.Batch, RLS-scoped to the nominator, T5); no reviews, review responses or draft
 * files are read. Writes go through Act: the Nomination row (SaveNew once as Draft, with SeasonID
 * taken from the presentation, NominatorUserID = me), its Nomination Responses in parallel (Update
 * or SaveNew, allSettled), then Submitted with the attestation and reasoning. Withdraw returns it to
 * Draft after MJConfirmService.Confirm. The agu_oep_core entity rules (DecideNominationSeason /
 * DecideNominationChange / DecideNominationResponseChange / DecideOwnedBySaver) re-check everything
 * on the server; a refusal is shown in the aria-live status and toasted.
 *
 * Editing a Submitted nomination saves in place and is not atomic: the responses are written while the
 * row stays Submitted, then the row is updated, so a refused response (or a crash in between) leaves a
 * Submitted nomination with a mix of old and new answers. T9 allows edits in both states while the
 * window is open (DecideNominationResponseChange). The committee never sees a mid-edit mix: its read
 * of Nominations and Nomination Responses is gated to Submitted nominations in seasons at Selecting or
 * later (tools/metadata/oep-permissions.js:64 committeeNominations, used at :161-162), and edits are
 * possible only in Nominating, so by the time the committee can read, no further edit can happen.
 */
@Component({
    standalone: false,
    selector: 'oep-nomination',
    templateUrl: './oep-nomination.component.html',
    styleUrl: './oep-nomination.component.scss',
})
export class OEPNominationComponent extends OEPViewBase implements OnChanges {
    private readonly confirm = inject(MJConfirmService);

    /** The Presentation being nominated (the `id=` of the `nomination` location). */
    @Input() PresentationID: string | null = null;

    VM: NominationVM | null = null;
    /** The presentation is not (or no longer) in one of my sessions. */
    NotFound = false;
    /** The form's picks and justifications, keyed by Review Question ID (bound with ngModel). */
    Answers: Record<string, OEPNomAnswer> = {};
    Additional = '';
    Attested = false;
    FormStatus: OEPStatusLine | null = null;
    readonly Outstanding = OEP_OUTSTANDING;

    private questions: OEPNomQuestionInput[] = [];
    private inputs: Omit<NominationInputs, 'Editing'> | null = null;
    private editing = false;
    private nominationID: string | null = null;
    private nominationStatus: OEPDraftOrSubmitted | null = null;
    /** The presentation's own SeasonID (a nomination must be in its presentation's season — DecideNominationSeason). */
    private seasonID: string | null = null;
    /** Review Question ID (upper) → its Nomination Responses row ID, kept current as writes succeed (a retry updates, never duplicates). */
    private readonly responseIDs = new Map<string, string>();
    private initialized = false;

    override ngOnInit(): void {
        this.initialized = true;
        super.ngOnInit();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized || !changes['PresentationID'] || changes['PresentationID'].isFirstChange()) return;
        this.reset();
        void this.Refresh();
    }

    private reset(): void {
        this.VM = null;
        this.inputs = null;
        this.editing = false;
        this.nominationID = null;
        this.nominationStatus = null;
        this.seasonID = null;
        this.responseIDs.clear();
        this.Answers = {};
        this.Additional = '';
        this.Attested = false;
        this.FormStatus = null;
    }

    protected async Load(): Promise<void> {
        const season = this.Context.Season;
        if (!season) return;
        if (!this.PresentationID) {
            this.NotFound = true;
            this.VM = null;
            return;
        }
        const me = OEPDataService.G(this.Context.Me.UserID);
        const sid = OEPDataService.G(season.ID);
        const pid = OEPDataService.G(this.PresentationID);
        const presWhere = `ID='${pid}' AND SeasonID='${sid}' AND ${OEP_LIVE_FILTER.Presentation} AND SessionID IN (SELECT SessionID FROM FeedbackPlatform.SessionNominator WHERE UserID='${me}')`;
        const mine = `SeasonID='${sid}' AND NominatorUserID='${me}' AND PresentationID='${pid}'`;
        const [presentations, participants, nominations, responses, questions, rubrics] = await this.Data.Batch([
            { EntityName: OEP_ENTITY.Presentations, ExtraFilter: presWhere, Fields: ['ID', 'Title', 'Section', 'SeasonID', 'Format', 'PresentationCode', 'PresenterUserID', 'PresenterUser'] },
            // The presenter's row (institution, career stage) — readable by the session's nominators (T5).
            { EntityName: OEP_ENTITY.Participants, ExtraFilter: `SeasonID='${sid}' AND UserID IN (SELECT PresenterUserID FROM FeedbackPlatform.Presentation WHERE ${presWhere})`, Fields: ['UserID', 'Institution', 'CareerStage'] },
            { EntityName: OEP_ENTITY.Nominations, ExtraFilter: mine, Fields: ['ID', 'Status', 'NoConflictAttested', 'AdditionalReasoning'] },
            { EntityName: OEP_ENTITY.NominationResponses, ExtraFilter: `NominationID IN (SELECT ID FROM FeedbackPlatform.Nomination WHERE ${mine})`, Fields: ['ID', 'ReviewQuestionID', 'NumericValue', 'TextValue'] },
            { EntityName: OEP_ENTITY.ReviewQuestions, ExtraFilter: NOMINATION_FORM, Fields: ['ID', 'Sequence', 'QuestionText', 'Description', 'ReviewQuestionType'], OrderBy: 'Sequence' },
            { EntityName: OEP_ENTITY.ReviewQuestionRubrics, ExtraFilter: `Status='Active' AND ReviewQuestionID IN (SELECT ID FROM FeedbackPlatform.ReviewQuestion WHERE ${NOMINATION_FORM})`, Fields: ['ID', 'ReviewQuestionID', 'RatingValue', 'RatingLabel', 'Criteria'], OrderBy: 'Sequence' },
        ]);

        const p = presentations[0];
        this.NotFound = p === undefined;
        if (!p) {
            this.VM = null;
            this.inputs = null;
            return;
        }
        const presenter = participants.find((x) => Up(Str(x, 'UserID')) === Up(Str(p, 'PresenterUserID')));
        this.seasonID = Str(p, 'SeasonID');
        const n = nominations[0];
        this.nominationID = n ? Str(n, 'ID') : this.nominationID;
        this.nominationStatus = n ? asStatus(StrOrNull(n, 'Status')) : this.nominationStatus;
        for (const x of responses) this.responseIDs.set(Up(Str(x, 'ReviewQuestionID')), Str(x, 'ID'));

        // The five rated criteria; the form's sixth question ("Additional reasoning") is Nomination.AdditionalReasoning.
        this.questions = questions.filter((q) => Str(q, 'ReviewQuestionType').startsWith('Rating Scale')).map((q) => {
            const qid = Str(q, 'ID');
            return {
                ID: qid,
                Sequence: Number(q['Sequence']),
                Text: Str(q, 'QuestionText'),
                Description: StrOrNull(q, 'Description'),
                Levels: rubrics.filter((l) => Up(Str(l, 'ReviewQuestionID')) === Up(qid)).map((l) => ({
                    ID: Str(l, 'ID'), Value: Number(l['RatingValue']), Label: Str(l, 'RatingLabel'), Criteria: Str(l, 'Criteria'),
                })),
            };
        });
        // The form starts from what is saved (fresh objects — ngModel mutates them).
        this.Answers = Object.fromEntries(this.questions.map((q) => {
            const x = responses.find((row) => Up(Str(row, 'ReviewQuestionID')) === Up(q.ID));
            return [q.ID, { Level: x ? NumOrNull(x, 'NumericValue') : null, Justification: x ? (StrOrNull(x, 'TextValue') ?? '') : '' }];
        }));
        this.Additional = n ? (StrOrNull(n, 'AdditionalReasoning') ?? '') : '';
        this.Attested = n ? n['NoConflictAttested'] === true || n['NoConflictAttested'] === 1 : false;

        this.inputs = {
            Phase: season.Phase, Timeline: season.Timeline, SeasonCode: season.Code,
            Presentation: {
                ID: Str(p, 'ID'), Title: Str(p, 'Title'), SectionName: Str(p, 'Section'), PresenterName: Str(p, 'PresenterUser'),
                Institution: presenter ? StrOrNull(presenter, 'Institution') : null,
                CareerStage: presenter ? StrOrNull(presenter, 'CareerStage') : null,
                Format: StrOrNull(p, 'Format'), Code: StrOrNull(p, 'PresentationCode'),
            },
            Questions: this.questions,
            Nomination: this.nominationID !== null && this.nominationStatus !== null ? { ID: this.nominationID, Status: this.nominationStatus } : null,
        };
        this.rebuild();
    }

    private rebuild(): void {
        this.VM = this.inputs ? BuildNominationVM({ ...this.inputs, Editing: this.editing }) : null;
    }

    Answer(questionID: string): OEPNomAnswer {
        this.Answers[questionID] ??= { Level: null, Justification: '' };
        return this.Answers[questionID];
    }

    Pick(questionID: string, level: number): void {
        this.Answer(questionID).Level = level;
        this.Changed();
    }

    ToggleAttested(): void {
        if (this.Busy) return;
        this.Attested = !this.Attested;
        this.Changed();
    }

    get CanSubmit(): boolean {
        return this.VM?.Mode === 'editing' && !this.Busy && CanSubmitNomination(this.questions, this.Answers, this.Attested);
    }

    /** "Edit nomination" on a submitted one: back to the form (it saves in place while the window is open — logic.js:697). */
    EditNomination(): void {
        if (!this.VM?.Done?.CanChange) return;
        this.editing = true;
        this.FormStatus = null;
        this.rebuild();
        this.Changed();
    }

    /** Create (once) and fill the nomination, then submit it. Mirrors DecideNominationChange: only while editing. */
    async Submit(): Promise<void> {
        const pid = this.PresentationID;
        const seasonID = this.seasonID;
        if (pid === null || seasonID === null || this.VM?.Mode !== 'editing' || this.Busy) return;
        if (!CanSubmitNomination(this.questions, this.Answers, this.Attested)) return;
        this.FormStatus = null;
        const answers = Object.fromEntries(this.questions.map((q) => [q.ID, { ...this.Answer(q.ID) }]));
        const additional = this.Additional.trim();
        const me = this.Context.Me.UserID;
        let partial: OEPPartialSaveError | null = null;
        const ok = await this.Act(async () => {
            let nid = this.nominationID;
            if (nid === null) {
                const created = await this.Data.SaveNew<FeedbackPlatformNominationEntity>(OEP_ENTITY.Nominations, (e) => {
                    e.SeasonID = seasonID; e.PresentationID = pid; e.NominatorUserID = me; e.Status = 'Draft';
                });
                nid = created.ID;
                this.nominationID = created.ID;
                this.nominationStatus = 'Draft';
            }
            const nominationID = nid;
            try {
                await this.saveResponses(nominationID, answers);
            } catch (err) {
                if (err instanceof OEPPartialSaveError) partial = err;
                throw err; // submit is never attempted after a refused answer
            }
            const wasSubmitted = this.nominationStatus === 'Submitted';
            await this.Data.Update<FeedbackPlatformNominationEntity>(OEP_ENTITY.Nominations, nominationID, (e) => {
                e.Status = 'Submitted';
                e.SubmittedAt = wasSubmitted ? (e.SubmittedAt ?? new Date()) : new Date();
                e.NoConflictAttested = true;
                e.AdditionalReasoning = additional.length > 0 ? additional : null;
            });
            this.nominationStatus = 'Submitted';
            this.editing = false;
        }, null, (m) => { this.FormStatus = { Text: m, Tone: 'error' }; });
        if (!ok) {
            const failed = partial as OEPPartialSaveError | null; // assigned inside the Act closure, which TS's flow analysis cannot see
            if (failed !== null) {
                // The other answers were saved: reload so the form reflects the server, then put back what
                // the nominator entered for the refused ones and the unsaved attestation and reasoning.
                await this.Refresh();
                for (const id of failed.FailedQuestionIDs) if (answers[id]) this.Answers[id] = { ...answers[id] };
                this.Additional = additional;
                this.Attested = true;
            }
        }
        this.Changed();
    }

    /** Every criterion in parallel; all settle before a failure is reported. TextValue is the Outstanding justification. */
    private async saveResponses(nominationID: string, answers: Readonly<Record<string, OEPNomAnswer>>): Promise<void> {
        const attempted: OEPNomQuestionInput[] = [];
        const work = this.questions.flatMap((q) => {
            const a = answers[q.ID];
            if (!a || a.Level === null) return [];
            attempted.push(q);
            const level = a.Level;
            const justification = level === OEP_OUTSTANDING ? a.Justification.trim() : '';
            const fill = (e: FeedbackPlatformNominationResponseEntity): void => {
                e.NumericValue = level; e.TextValue = justification.length > 0 ? justification : null;
            };
            const existing = this.responseIDs.get(Up(q.ID)) ?? null;
            return [existing !== null
                ? this.Data.Update<FeedbackPlatformNominationResponseEntity>(OEP_ENTITY.NominationResponses, existing, fill).then(() => undefined)
                : this.Data.SaveNew<FeedbackPlatformNominationResponseEntity>(OEP_ENTITY.NominationResponses, (e) => { e.NominationID = nominationID; e.ReviewQuestionID = q.ID; fill(e); })
                    .then((row) => { this.responseIDs.set(Up(q.ID), row.ID); })];
        });
        const results = await Promise.allSettled(work);
        const failed = results.flatMap((x, i) => (x.status === 'rejected' ? [{ Question: attempted[i], Reason: x.reason }] : []));
        if (failed.length === 0) return;
        const first = failed[0].Reason;
        const message = first instanceof Error ? first.message : String(first);
        throw new OEPPartialSaveError(
            PartialSaveMessage(failed.map((f) => QuestionShortLabel(f.Question.Sequence)), attempted.length, message),
            failed.map((f) => f.Question.ID),
        );
    }

    /**
     * Withdraw a submitted nomination back to Draft (task 27 brief; DecideNominationChange allows it while the
     * window is open). The attestation is cleared too, so a resubmission attests again.
     */
    async Withdraw(): Promise<void> {
        const nid = this.nominationID;
        if (nid === null || !this.VM?.Done?.CanChange || this.Busy) return;
        const yes = await this.confirm.Confirm({
            title: 'Withdraw this nomination?', message: 'Your section committee will not see it.',
            type: 'warning', confirmText: 'Withdraw', cancelText: 'Keep it',
        });
        if (!yes) return;
        this.FormStatus = null;
        const ok = await this.Act(async () => {
            await this.Data.Update<FeedbackPlatformNominationEntity>(OEP_ENTITY.Nominations, nid, (n) => { n.Status = 'Draft'; n.SubmittedAt = null; n.NoConflictAttested = false; });
            this.nominationStatus = 'Draft';
            this.editing = false;
        }, null, (m) => { this.FormStatus = { Text: m, Tone: 'error' }; });
        if (ok) this.FormStatus = { Text: 'Nomination withdrawn.', Tone: 'ok' };
        this.Changed();
    }

    Back(): void {
        this.Go('nominate');
    }
}
